import React, { useEffect, useState } from 'react';
import { firestore } from '../firebase';

// RSS Feed Generator Component
// This component generates an RSS feed from Firestore blogs
// Access at /rss.xml or /feed.xml

const RSSFeed = () => {
  const [rssContent, setRssContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateRSS = async () => {
      try {
        // Fetch all published blogs from Firestore
        const snapshot = await firestore
          .collection('blogs')
          .where('published', '==', true)
          .orderBy('createdAt', 'desc')
          .limit(50)
          .get();

        const blogs = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            subtitle: data.subtitle || data.excerpt || data.title,
            body: data.body || '',
            heroImage: data.heroImage || '',
            slug: data.slug,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
            author: data.author || 'Lunar Electric'
          };
        });

        // Generate RSS XML
        const rssItems = blogs.map(blog => {
          const pubDate = blog.createdAt.toUTCString();
          const link = `https://lunarelectric.in/blogs/${blog.slug}`;
          
          // Strip HTML tags for description (first 300 chars)
          const plainText = blog.body.replace(/<[^>]*>/g, '').substring(0, 300);
          const description = blog.subtitle || plainText || blog.title;
          
          // Escape XML special characters
          const escapeXml = (str) => {
            if (!str) return '';
            return str
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&apos;');
          };

          const imageTag = blog.heroImage 
            ? `      <enclosure url="${escapeXml(blog.heroImage)}" type="image/jpeg" />`
            : '';

          return `    <item>
      <title><![CDATA[${blog.title}]]></title>
      <link>${link}</link>
      <guid isPermaLink="true">${link}</guid>
      <description><![CDATA[${description}]]></description>
      <pubDate>${pubDate}</pubDate>
      <author>${blog.author}</author>
${imageTag}
    </item>`;
        }).join('\n');

        const rssXML = `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0" xmlns:content="http://purl.org/rss/1.0/modules/content/" xmlns:atom="http://www.w3.org/2005/Atom">
  <channel>
    <title>Lunar Electric Blog</title>
    <link>https://lunarelectric.in/blogs</link>
    <description>Latest solar energy tips, industry news, and insights from Lunar Electric</description>
    <language>en-US</language>
    <lastBuildDate>${new Date().toUTCString()}</lastBuildDate>
    <atom:link href="https://lunarelectric.in/rss.xml" rel="self" type="application/rss+xml" />
    <image>
      <url>https://lunarelectric.in/logo512.png</url>
      <title>Lunar Electric</title>
      <link>https://lunarelectric.in</link>
    </image>
${rssItems}
  </channel>
</rss>`;

        setRssContent(rssXML);
        setLoading(false);
      } catch (error) {
        console.error('Error generating RSS feed:', error);
        setLoading(false);
      }
    };

    generateRSS();
  }, []);

  // Set content type header when component mounts
  useEffect(() => {
    // This is a workaround - in production, you'd want to use server-side rendering
    // or a static file for proper RSS feed serving
    const metaContentType = document.querySelector('meta[http-equiv="Content-Type"]');
    if (metaContentType) {
      metaContentType.setAttribute('content', 'application/rss+xml; charset=utf-8');
    }
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Generating RSS feed...</p>
      </div>
    );
  }

  // Return XML as preformatted text
  // Note: For production, consider generating this as a static file or using server-side rendering
  return (
    <pre style={{ 
      whiteSpace: 'pre-wrap', 
      fontFamily: 'monospace', 
      fontSize: '12px',
      margin: 0,
      padding: '1rem',
      background: '#fff'
    }}>
      {rssContent}
    </pre>
  );
};

export default RSSFeed;
